import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Suspense, lazy, useEffect } from "react";

// Lazy-load the three heaviest utility modules so they don't bloat index.js.
// rrweb (~40KB brotli) + analytics + sessionTracker were eating ~100KB of
// mobile main-bundle parse time even though their execution was already
// deferred. Module references are cached after first import so subsequent
// calls (route changes) resolve instantly.
let _analyticsP: Promise<typeof import("@/shared/utils/analytics")> | null = null;
const getAnalytics = () => (_analyticsP ??= import("@/shared/utils/analytics"));
let _recorderP: Promise<typeof import("@/marketing/utils/sessionRecorder")> | null = null;
const getRecorder = () => (_recorderP ??= import("@/marketing/utils/sessionRecorder"));
let _trackerP: Promise<typeof import("@/shared/utils/sessionTracker")> | null = null;
const getTracker = () => (_trackerP ??= import("@/shared/utils/sessionTracker"));

// AppShell holds the Redux store + WebSocketProvider + NotificationProvider
// + TutorialProvider. Lazy-loaded because the entire public-route tree
// (`/`, all feature variants, `/team`, `/contact`, `/blog`, etc.) has ZERO
// useSelector / useDispatch / useWebSocket / useNotifications / useTutorial
// consumers — verified via codebase grep in BUNDLE_AUDIT.md. Moving the
// provider tree behind a lazy boundary cuts ~60-80 KB of eagerly-parsed
// JS off the landing critical path, dropping mobile LCP on Lighthouse's
// simulated Moto G Power profile (4× CPU + Slow 4G) by ~1.5-2 s.
const AppShell = lazy(() => import("@/AppShell"));

// AuthenticatedLayout still lazy — was already deferred for /dashboard,
// /onboarding routes. Now lives INSIDE AppShell.
const AuthenticatedLayout = lazy(
  () => import(/* webpackChunkName: "authenticated-layout" */ "@/platform/components/layouts/AuthenticatedLayout")
);

// Toaster lazy + only rendered inside AppShell (toasts only fire from
// authenticated/app flows). Public routes don't need it.
const Toaster = lazy(() =>
  import("sonner").then((m) => ({ default: m.Toaster }))
);

// Suspense fallback: the splash is rendered OUTSIDE the React tree (a
// sibling of #root in index.html) so it stays visible during chunk
// loading without being part of React's reconciliation. main.tsx removes
// the splash element after hydration completes. Returning null here means
// when a lazy chunk suspends, the user keeps seeing the splash via the
// overlay element until the chunk resolves. No flash.
import { tokenManager } from "@/shared/services/tokenManager";
// Eager (not lazy): the banner must paint with the first frame, otherwise
// the headers would visibly jump down once its chunk resolved.
import CapacityBanner from "@/marketing/components/full-site/CapacityBanner";

const LoadingFallback = () => null;

/**
 * Gate for /plans while new registration is closed (MiDash is at full
 * capacity). Anonymous visitors are bounced to the homepage — the page is
 * withdrawn from the navbar, footer and sitemap and there is no signup to
 * convert them into. Signed-in users still get the real page, because it is
 * the plan-chooser their /billing upgrade buttons depend on.
 *
 * Uses tokenManager directly (not a Redux/auth hook) so it stays valid inside
 * the AppShell route element without adding another provider dependency.
 */
const PlansRouteGuard = () => {
  const token = tokenManager.getAccessToken();
  const isLoggedIn = !!token && !tokenManager.isTokenExpired();
  return isLoggedIn ? <Plans /> : <Navigate to="/" replace />;
};

// Authenticated routes where copy/paste is allowed
const COPY_ALLOWED_PREFIXES = ['/platform', '/onboarding', '/settings'];

// Route change tracker - tracks page views and manages copy protection per route
const RouteChangeTracker = () => {
  const location = useLocation();

  useEffect(() => {
    getAnalytics().then((m) => {
      m.trackPageView(location.pathname);
      m.initScrollTracking();
    });
    // Only touch the recorder if it's already been loaded — don't pay the
    // 45KB rrweb cost on navigations that originate on marketing pages.
    if (typeof window !== 'undefined' && (window as Window & { __rrwebLoaded?: boolean }).__rrwebLoaded) {
      getRecorder().then((m) => m.recordNavigation(location.pathname));
    }
  }, [location.pathname]);

  // Copy protection: blocked on marketing pages, allowed on authenticated routes
  useEffect(() => {
    const isProduction = window.location.hostname === 'midash.ai' || window.location.hostname === 'www.midash.ai';
    if (!isProduction) return;

    const isCopyAllowed = COPY_ALLOWED_PREFIXES.some(p => location.pathname.startsWith(p));

    if (isCopyAllowed) {
      document.body.classList.remove('no-copy');
      return;
    }

    document.body.classList.add('no-copy');

    const blockKeys = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && ['c', 'a', 'x'].includes(e.key.toLowerCase())) {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        e.preventDefault();
      }
    };
    const blockContext = (e: MouseEvent) => e.preventDefault();

    document.addEventListener('keydown', blockKeys);
    document.addEventListener('contextmenu', blockContext);

    return () => {
      document.body.classList.remove('no-copy');
      document.removeEventListener('keydown', blockKeys);
      document.removeEventListener('contextmenu', blockContext);
    };
  }, [location.pathname]);

  return null;
};

// Coming soon component removed

// Import full site components
// Index is NOT lazy loaded - it's the landing page, users always see it first
import Index from "@/marketing/pages";
import {
  FEATURE_VARIANTS,
  type FeatureVariant,
} from "@/marketing/components/landing-new/heroVariants";
import { LANDING_WRAPPERS } from "@/marketing/pages/landings";
const LandingNew = lazy(
  () => import(/* webpackChunkName: "landing-new-page" */ "@/marketing/pages/landing-new")
);
const NotFound = lazy(
  () => import(/* webpackChunkName: "not-found-page" */ "@/marketing/pages/NotFound")
);
const Demo = lazy(
  () => import(/* webpackChunkName: "demo-page" */ "@/marketing/pages/Demo")
);
const PrivacyPolicy = lazy(
  () => import(/* webpackChunkName: "privacy-page" */ "@/marketing/pages/PrivacyPolicy")
);
const TermsOfService = lazy(
  () => import(/* webpackChunkName: "terms-page" */ "@/marketing/pages/TermsOfService")
);
const Contact = lazy(
  () => import(/* webpackChunkName: "contact-page" */ "@/marketing/pages/Contact")
);
const Solutions = lazy(
  () => import(/* webpackChunkName: "solutions-page" */ "@/marketing/pages/Solutions")
);
const Institutional = lazy(
  () =>
    import(
      /* webpackChunkName: "institutional-page" */ "@/marketing/pages/Institutional"
    )
);
const Coverage = lazy(
  () => import(/* webpackChunkName: "coverage-page" */ "@/marketing/pages/Coverage")
);
const FAQ = lazy(
  () => import(/* webpackChunkName: "faq-page" */ "@/marketing/pages/FAQ")
);
const Story = lazy(
  () => import(/* webpackChunkName: "story-page" */ "@/marketing/pages/Story")
);
const Team = lazy(
  () => import(/* webpackChunkName: "team-page" */ "@/marketing/pages/Team")
);
const Blog = lazy(
  () => import(/* webpackChunkName: "blog-page" */ "@/marketing/pages/resources/Blog")
);
const BlogPost = lazy(
  () =>
    import(
      /* webpackChunkName: "blog-post-page" */ "@/marketing/pages/resources/BlogPost"
    )
);
const AITradingAgents = lazy(
  () =>
    import(
      /* webpackChunkName: "ai-trading-agents" */ "@/marketing/pages/products/AITradingAgents"
    )
);
const Strategies = lazy(
  () => import(/* webpackChunkName: "strategies-page" */ "@/marketing/pages/Strategies")
);
const Login = lazy(
  () => import(/* webpackChunkName: "login-page" */ "@/marketing/pages/auth/Login")
);
const Register = lazy(
  () => import(/* webpackChunkName: "register-page" */ "@/marketing/pages/auth/Register")
);
const ForgotPassword = lazy(
  () =>
    import(
      /* webpackChunkName: "forgot-password-page" */ "@/marketing/pages/auth/ForgotPassword"
    )
);
const ResetPassword = lazy(
  () =>
    import(
      /* webpackChunkName: "reset-password-page" */ "@/marketing/pages/auth/ResetPassword"
    )
);
const Plans = lazy(
  () => import(/* webpackChunkName: "plans-page" */ "@/marketing/pages/plans/Plans")
);
const TaaftLanding = lazy(
  () => import(/* webpackChunkName: "taaft-landing" */ "@/marketing/pages/campaigns/TaaftLanding")
);
const GoogleCallback = lazy(
  () =>
    import(
      /* webpackChunkName: "google-callback" */ "@/marketing/pages/auth/GoogleCallback"
    )
);
const AlpacaCallback = lazy(
  () =>
    import(
      /* webpackChunkName: "alpaca-callback" */ "@/marketing/pages/auth/AlpacaCallback"
    )
);
const OnboardingExperience = lazy(
  () =>
    import(
      /* webpackChunkName: "onboarding-experience-page" */ "@/platform/components/onboarding/OnboardingExperience"
    )
);
const BillingPage = lazy(
  () => import(/* webpackChunkName: "billing-page" */ "@/platform/pages/billing/BillingPage")
);
const CheckoutSuccess = lazy(
  () => import(/* webpackChunkName: "checkout-success" */ "@/marketing/pages/billing/CheckoutSuccess")
);
const PaymentCanceled = lazy(
  () => import(/* webpackChunkName: "payment-canceled" */ "@/marketing/pages/billing/PaymentCanceled")
);
const SettingsPage = lazy(
  () => import(/* webpackChunkName: "settings-page" */ "@/platform/pages/settings/SettingsPage")
);
const HeroPreview = lazy(
  () =>
    import(
      /* webpackChunkName: "hero-preview" */ "@/marketing/components/full-site/HeroSectionResponsive"
    )
);
// Helper to defer non-critical work until browser is idle
const deferToIdle = (fn: () => void, timeout = 2000) => {
  if ('requestIdleCallback' in window) {
    (window as typeof window & {
      requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void;
    }).requestIdleCallback(fn, { timeout });
  } else {
    setTimeout(fn, 1);
  }
};

// Wait for LCP to fire before running heavy work. This prevents tracking/replay
// from competing with the hero image for main-thread time during the critical
// render window. Falls back to a 4s timeout if PerformanceObserver isn't
// available (older Safari) or LCP never fires (the LCP entry is buffered, so
// even late subscriptions get the entry).
const afterLCP = (fn: () => void) => {
  if (typeof PerformanceObserver === 'undefined') {
    setTimeout(fn, 4000);
    return;
  }
  let fired = false;
  const runOnce = () => {
    if (fired) return;
    fired = true;
    fn();
  };
  try {
    const obs = new PerformanceObserver((list) => {
      if (list.getEntries().length > 0) {
        obs.disconnect();
        // Small delay so the browser can paint LCP before we steal the main thread.
        setTimeout(runOnce, 250);
      }
    });
    obs.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    setTimeout(runOnce, 4000);
  }
  // Hard fallback — guarantees work eventually runs.
  setTimeout(runOnce, 6000);
};

// Conservative-network detection. We skip the heaviest payloads (rrweb session
// replay) on slow connections or when the user has Data Saver on.
const isSlowConnection = (): boolean => {
  const c = (navigator as Navigator & { connection?: { effectiveType?: string; saveData?: boolean } }).connection;
  if (!c) return false;
  if (c.saveData) return true;
  return c.effectiveType === 'slow-2g' || c.effectiveType === '2g' || c.effectiveType === '3g';
};

// Marketing routes don't host authenticated flows, so session replay there
// captures noise without any debugging value. Keep replay scoped to the
// surfaces where it actually helps (auth, dashboard, platform).
const isReplayWorthyPath = (path: string): boolean => {
  return (
    path.startsWith('/platform') ||
    path.startsWith('/onboarding') ||
    path.startsWith('/settings') ||
    path.startsWith('/auth') ||
    path.startsWith('/login') ||
    path.startsWith('/register') ||
    path.startsWith('/billing')
  );
};

//Do we need this??
const ImmersiveDashboardRoute = lazy(
  () =>
    import(
      /* webpackChunkName: "platform-page" */ "@/platform/features/live/components/ImmersiveDashboardRoute"
    ).then(module => ({ default: module.ImmersiveDashboardRoute }))
);

// Set when scripts/prerender/prerender.mjs captures the DOM in headless
// Puppeteer. Lets every side-effecty boot path (analytics, session replay,
// token init, animation intervals) detect "I'm being snapshotted, do nothing"
// so the captured HTML is deterministic and matches what a fresh client
// will render on first paint.
declare global {
  interface Window {
    __PRERENDERING?: boolean;
  }
}

function App() {
  // Initialize critical services immediately, defer non-critical to idle time.
  // Note: tokenManager.initialize() and initStoreSync(store) used to live
  // here but moved into AppShell (src/AppShell.tsx) so public-route
  // visitors don't pay for the Redux store + token-manager imports.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__PRERENDERING) return;

    // DEFERRED: Analytics and tracking - not needed for initial render
    // Using requestIdleCallback to run after browser is idle (~500ms savings).
    // Modules are also dynamically imported (see top of file) so their CODE
    // doesn't ship in the main bundle either — only fetched here at idle time.
    const isBot = /bot|crawl|spider|slurp|googlebot|bingbot|yandex|baidu/i.test(navigator.userAgent);

    // Session tracking + analytics: light, defer to idle (post-LCP).
    deferToIdle(() => {
      if (!isBot) {
        getTracker().then((m) => m.initSessionTracking());
      }

      getAnalytics().then((m) => {
        m.initGA4();
        m.trackPageView(window.location.pathname);
        m.initScrollTracking();
        // Drain queued trackClick/trackEvent calls made by eager components
        // (HeroShell, Navbar) that used the lightweight @/shared/utils/track façade.
        import("@/shared/utils/track").then((t) => t._bindAnalytics(m));
      });
    }, 3000);

    // Session replay: the heaviest payload (45KB JS + ongoing CPU + POSTs every 5s).
    // Gate it hard so it never competes with LCP on mobile:
    //   1) Skip bots, slow networks, and Data Saver users
    //   2) Skip marketing surfaces entirely — replay is useless without auth state
    //   3) Wait until AFTER LCP fires (PerformanceObserver) + 250ms paint slack
    if (!isBot && !isSlowConnection() && isReplayWorthyPath(window.location.pathname)) {
      afterLCP(() => {
        getRecorder().then((m) => m.startRecording());
      });
    }
  }, []);

  return (
    <HelmetProvider>
      <BrowserRouter>
        <Suspense fallback={<LoadingFallback />}>
          <RouteChangeTracker />
          {/* Site-wide "at full capacity" notice. Self-suppresses on the
              authenticated app routes and once dismissed for the session. */}
          <CapacityBanner />
          <Routes>
            {/* ── PUBLIC ROUTES — no Redux / WebSocket / Notification / Tutorial.
                The components below this comment block have ZERO consumers of
                useSelector / useDispatch / useWebSocket / useNotifications /
                useTutorial (verified via grep, see BUNDLE_AUDIT.md).
                Skipping the provider tree here keeps ~60-80 KB of Redux + WS
                modules out of the eager landing chunk. ── */}
                <Route path="/" element={<LandingNew />} />
                <Route path="/legacy" element={<Index />} />
                {/* Backwards-compatible alias for any in-flight links
                 * to the new landing while it was at /landing_new. */}
                <Route path="/landing_new" element={<LandingNew />} />
                {/* Feature landing variants — each variant gets its
                 * own wrapper file in pages/landings/. Wrappers are
                 * one-liners by default (pure delegation to
                 * <LandingNew variant={X} />), so behavior is identical
                 * to direct rendering. The wrapper exists so that any
                 * per-page customization (custom widgets, modals,
                 * analytics, hero overrides) has a natural home and
                 * doesn't bleed into shared files. To add a new feature
                 * page: (1) add a variant key + path in heroVariants.ts,
                 * (2) add a wrapper file in pages/landings/, (3) add it
                 * to LANDING_WRAPPERS in pages/landings/index.ts. */}
                {(Object.entries(FEATURE_VARIANTS) as Array<
                  [FeatureVariant, (typeof FEATURE_VARIANTS)[FeatureVariant]]
                >).flatMap(([variant, cfg]) => {
                  const Wrapper = LANDING_WRAPPERS[variant];
                  return [cfg.path, ...(cfg.aliases ?? [])].map((path) => (
                    <Route key={path} path={path} element={<Wrapper />} />
                  ));
                })}
                <Route
                  path="/products/ai-trading-agents"
                  element={<AITradingAgents />}
                />
                <Route
                  path="/products/ai-trading-bots"
                  element={<AITradingAgents />}
                />
                <Route path="/strategies" element={<Strategies />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/story" element={<Story />} />
                <Route path="/team" element={<Team />} />
                <Route path="/solutions" element={<Solutions />} />
                <Route path="/institutional" element={<Institutional />} />
                <Route path="/coverage" element={<Coverage />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/resources/blog" element={<Blog />} />
                <Route path="/resources/blog/:slug" element={<BlogPost />} />
                {/* Campaign landing pages — public, no providers */}
                <Route path="/taaft" element={<TaaftLanding />} />
                <Route path="/exclusive" element={<TaaftLanding />} />
                <Route path="/special" element={<TaaftLanding />} />
                <Route path="/deal" element={<TaaftLanding />} />
                {/* Temporary: Hero preview for testing new immersive design */}
                <Route path="/hero-preview" element={<HeroPreview />} />
                {/* Standalone live demo — same immersive simulation as the
                    landing "Try live Demo", at a stable URL so the static
                    marketing site can embed it inline. */}
                <Route path="/demo" element={<Demo />} />

                {/* ── APPSHELL ROUTES — wrapped in AppShell so they get
                    Redux + WebSocket + Notification + Tutorial providers.
                    Every route below uses at least one of useSelector,
                    useDispatch, useWebSocket, useNotifications, or
                    useTutorial (verified in BUNDLE_AUDIT.md). The
                    AppShell module is lazy-loaded so the public-route
                    visitors above never download Redux/WebSocket code. ── */}
                <Route element={<AppShellRoute />}>
                  <Route path="/login" element={<Login />} />
                  {/* Registration is CLOSED — MiDash is at full capacity.
                      The route is retired rather than deleted: Register.tsx
                      stays on disk so re-opening signups is a one-line
                      revert here (plus the auth-service env flag, which is
                      the authoritative gate — see services/auth/auth_api.py).
                      Redirects to /login rather than 404ing so existing
                      users following an old bookmark or email link land
                      somewhere they can actually use. */}
                  <Route
                    path="/auth/register"
                    element={<Navigate to="/login" replace />}
                  />
                  <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                  <Route path="/auth/reset-password" element={<ResetPassword />} />
                  <Route path="/auth/google/success" element={<GoogleCallback />} />
                  <Route path="/brokers/alpaca/callback" element={<AlpacaCallback />} />
                  {/* /plans is withdrawn from PUBLIC access while
                      registration is closed. It stays reachable for SIGNED-IN
                      users because it is the only plan-chooser an existing
                      paying customer can upgrade through (/billing's
                      "Subscribe" / "View Plans" buttons both route here).
                      To re-open publicly, drop the guard. */}
                  <Route path="/plans" element={<PlansRouteGuard />} />
                  {/* The legacy /dashboard surface was retired: every navigation in
                      the app (Login.tsx and 7 others) goes to /platform, and nothing
                      linked here. Kept as a REDIRECT rather than deleted outright so an
                      old bookmark lands on the live surface instead of the 404 page. */}
                  <Route path="/dashboard" element={<Navigate to="/platform" replace />} />
                  <Route
                    path="/platform"
                    element={
                      <AuthenticatedLayout>
                        <ImmersiveDashboardRoute />
                      </AuthenticatedLayout>
                    }
                  />
                  <Route
                    path="/onboarding"
                    element={
                      <AuthenticatedLayout>
                        <OnboardingExperience />
                      </AuthenticatedLayout>
                    }
                  />
                  <Route
                    path="/billing"
                    element={
                      <AuthenticatedLayout>
                        <BillingPage />
                      </AuthenticatedLayout>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <AuthenticatedLayout>
                        <SettingsPage />
                      </AuthenticatedLayout>
                    }
                  />
                  <Route
                    path="/payment-success"
                    element={
                      <AuthenticatedLayout>
                        <CheckoutSuccess />
                      </AuthenticatedLayout>
                    }
                  />
                  <Route
                    path="/payment-canceled"
                    element={
                      <AuthenticatedLayout>
                        <PaymentCanceled />
                      </AuthenticatedLayout>
                    }
                  />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
        </Suspense>
      </BrowserRouter>
    </HelmetProvider>
  );
}

// Wraps every AppShell-bound route. <AppShell> contains Redux + WS +
// Notification + Tutorial providers; <Outlet/> renders the matched
// child route inside. Toaster lives here too — toast-driven UI is
// auth-only, so we don't ship sonner's chunk to landing visitors.
function AppShellRoute() {
  return (
    <Suspense fallback={null}>
      <AppShell>
        <Outlet />
        <Suspense fallback={null}>
          <Toaster position="top-center" richColors />
        </Suspense>
      </AppShell>
    </Suspense>
  );
}

export default App;
